/**
 * CollectorUp — Meta Pixel client-side integration.
 *
 * ONE global Meta Pixel per session:
 *   • `public/index.html` installs and initialises fbq('init', PIXEL_ID)
 *     synchronously at initial page load (skipping the /admin surface).
 *   • This module exposes helpers used by React code for SPA navigation
 *     and conversion events. It never re-initialises the pixel — the
 *     hardcoded `__cuFBQBooted` flag on window prevents that.
 *
 * Public API:
 *   - trackPixelPageView(pathname)   → fires PageView on route change.
 *   - trackPixelEvent(name, params, options)
 *   - META_PIXEL_ID                  → constant, exported for tests.
 *
 * Design notes:
 *   • Pixel ID is a PUBLIC client-side identifier by Meta's design.
 *     It is safe to embed in the JS bundle exactly like the Shopify
 *     storefront token in `ShopifyBuyButton.jsx`.
 *   • Admin surfaces (/admin*) are 100% excluded — matches the same
 *     GA4/Clarity rule already enforced in `analytics.js` so Gil's
 *     admin activity never pollutes ads reporting.
 *   • Idempotency: PageView is deduped per pathname within a session
 *     to survive React StrictMode double-effects; conversion events
 *     accept an `event_id` for cross-provider (browser + server-side
 *     Conversions API) deduplication.
 */

export const META_PIXEL_ID = "1418866500132317";

// Per-session cache — never fire the same PageView twice for the
// same normalised path (StrictMode double-mounts, replace-state churn).
// The initial HTML fires `fbq('track','PageView')` synchronously in
// `public/index.html` for the visitor's landing URL. We claim that
// path on module load so the FIRST `trackPixelPageView()` call from
// AnalyticsRouteTracker is a silent no-op and only later SPA route
// changes emit a fresh PageView.
let _lastPageViewPath = (() => {
  if (typeof window === "undefined" || !window.location) return null;
  if (!window.__cuFBQBooted) return null;      // pixel wasn't booted (e.g. /admin)
  return (window.location.pathname || "/").toLowerCase();
})();

// Guard so calling `trackPixelEvent(name, params, {once:true})` from
// two async callbacks (e.g. React 18 double-invoke) never emits twice.
const _onceGuard = new Set();

function _isAdminPath(p) {
  if (typeof p !== "string" || !p) return false;
  const path = p.split("?")[0].toLowerCase();
  return path === "/admin" || path.startsWith("/admin/");
}

function _currentPath() {
  if (typeof window === "undefined" || !window.location) return "";
  return window.location.pathname || "";
}

/**
 * Best-effort access to `window.fbq`. If the base tag in
 * `public/index.html` was never loaded (e.g. because the visitor
 * landed on `/admin` first and later navigated to a public route
 * inside the SPA), boot the SDK lazily so subsequent PageView /
 * conversion events still reach Meta.
 */
function _ensureFbq() {
  if (typeof window === "undefined") return null;
  if (typeof window.fbq === "function") return window.fbq;
  if (typeof window.__cuInitFBQ === "function") {
    try { window.__cuInitFBQ(); } catch (_) { /* swallow */ }
  }
  return typeof window.fbq === "function" ? window.fbq : null;
}

/**
 * Fire a Meta Pixel PageView for the current SPA route.
 * Called from AnalyticsRouteTracker on every location change.
 */
export function trackPixelPageView(rawPath) {
  if (typeof window === "undefined") return;
  const path = rawPath || (_currentPath() + window.location.search);
  const normalised = (path || "/").split("?")[0].toLowerCase();
  if (_isAdminPath(normalised) || _isAdminPath(_currentPath())) return;
  if (_lastPageViewPath === normalised) return;
  _lastPageViewPath = normalised;
  const fbq = _ensureFbq();
  if (!fbq) return;
  try { fbq("track", "PageView"); } catch (_) { /* swallow */ }
}

/**
 * Fire a Meta Pixel conversion event.
 *
 * @param {string} name    - Standard event name (ViewContent, Lead, Purchase, InitiateCheckout, …).
 * @param {object} params  - custom_data payload (value, currency, content_name …).
 * @param {object} options - { eventId?: string, once?: boolean }.
 *                           When `eventId` is provided it is echoed as
 *                           `{eventID: …}` to Meta for browser↔server
 *                           deduplication. When `once=true`, the same
 *                           (name, eventId) combination will only fire
 *                           once per JS runtime.
 */
export function trackPixelEvent(name, params, options) {
  if (typeof window === "undefined") return;
  if (!name) return;
  if (_isAdminPath(_currentPath())) return;
  const opts = options || {};
  const eventId = opts.eventId ? String(opts.eventId) : null;
  if (opts.once && eventId) {
    const key = `${name}::${eventId}`;
    if (_onceGuard.has(key)) return;
    _onceGuard.add(key);
  }
  const fbq = _ensureFbq();
  if (!fbq) return;
  try {
    if (eventId) {
      fbq("track", name, params || {}, { eventID: eventId });
    } else {
      fbq("track", name, params || {});
    }
  } catch (_) { /* swallow — never break the page */ }
}
